import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import Header from "../../components/Header";
import Footer from "../../components/Footer";
import PageBanner from "../../components/PageBanner";
import CountUp from "react-countup";
import { getNbaVisitData } from "../../services/data.service";
import { mediaUrl } from "../../services/assetUrl";
import "./nbaNew.css";

const EASE = [0.22, 1, 0.36, 1];

const CATEGORY_META = {
  "quick-links": { icon: "link", accent: "#3270FC" },
  "faculty-details": { icon: "faculty", accent: "#F6872A" },
  "placement-details": { icon: "placement", accent: "#10b981" },
  students: { icon: "students", accent: "#8b5cf6" },
  "support-staff": { icon: "staff", accent: "#0e2455" },
};

const slugify = (value) =>
  value.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

const buildCategories = (collections) => {
  const categories = [];

  collections.forEach((collection) => {
    if (collection.title.startsWith("Student Lists")) {
      let studentGroup = categories.find((item) => item.id === "students");
      if (!studentGroup) {
        studentGroup = {
          id: "students",
          title: "Student Details",
          sections: [],
          files: [],
          fileCount: 0,
        };
        categories.push(studentGroup);
      }

      studentGroup.sections.push({
        title: collection.title.replace("Student Lists — ", ""),
        files: collection.files,
      });
      studentGroup.fileCount += collection.files.length;
      return;
    }

    categories.push({
      id: slugify(collection.title),
      title: collection.title,
      sections: null,
      files: collection.files,
      fileCount: collection.files.length,
    });
  });

  return categories;
};

const IconSearch = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="7" />
    <path d="M20 20l-3-3" />
  </svg>
);

const IconPdf = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
    <path d="M14 2v6h6M9 13h6M9 17h4" />
  </svg>
);

const IconDownload = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3" />
  </svg>
);

const CategoryIcon = ({ type }) => {
  const icons = {
    link: <path d="M10 13a5 5 0 007.54.54l3-3a5 5 0 00-7.07-7.07l-1.72 1.71" />,
    faculty: <><path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" /></>,
    placement: <><rect x="2" y="7" width="20" height="14" rx="2" /><path d="M16 7V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v2" /></>,
    students: <><path d="M22 10v6M2 10l10-5 10 5-10 5z" /><path d="M6 12v5c0 1.7 2.7 3 6 3s6-1.3 6-3v-5" /></>,
    staff: <><path d="M16 21v-2a4 4 0 00-4-4H6a4 4 0 00-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M22 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" /></>,
  };

  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
      {icons[type] || icons.link}
    </svg>
  );
};

const NBAVisitData = () => {
  // DB-only: the whole page comes from the backend singleton.
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const hero = content?.hero || {};
  const documents = content?.documents || {};
  const categories = useMemo(
    () => (documents.collections ? buildCategories(documents.collections) : []),
    [documents.collections]
  );
  const totalDocs = categories.reduce((sum, category) => sum + category.fileCount, 0);

  const [activeId, setActiveId] = useState("");
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = "NBA Visit Data | NCET";
  }, []);

  // Fetch the backend content (singleton).
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await getNbaVisitData();
        const doc = res?.data?.data || res?.data || null;
        if (active && doc && doc.documents) setContent(doc);
      } catch (e) {
        // leave content null -> unavailable state
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, []);

  // Default the active category to the first once content arrives.
  useEffect(() => {
    if (!activeId && categories.length) setActiveId(categories[0].id);
  }, [categories, activeId]);

  const activeCategory = categories.find((category) => category.id === activeId) || categories[0];
  const normalizedQuery = searchQuery.trim().toLowerCase();

  const filterFiles = (files) => {
    if (!normalizedQuery) return files;
    return files.filter((file) => file.title.toLowerCase().includes(normalizedQuery));
  };

  const visibleSections = useMemo(() => {
    if (!activeCategory) return [];

    if (activeCategory.sections) {
      return activeCategory.sections
        .map((section) => ({
          ...section,
          files: filterFiles(section.files),
        }))
        .filter((section) => section.files.length > 0);
    }

    const files = filterFiles(activeCategory.files);
    return files.length ? [{ title: null, files }] : [];
  }, [activeCategory, normalizedQuery]);

  const visibleFileCount = visibleSections.reduce((sum, section) => sum + section.files.length, 0);
  const activeMeta = CATEGORY_META[activeCategory?.id] || CATEGORY_META["quick-links"];

  // Loading / unavailable states (DB-only, no bundled fallback).
  if (loading || !content) {
    return (
      <div className="nvb-page">
        <Header parentMenu="outreach" topbarEnable="enable" />
        <div className="container" style={{ minHeight: "50vh", display: "flex", alignItems: "center", justifyContent: "center", textAlign: "center", padding: "4rem 0" }}>
          <p style={{ fontSize: "1.05rem", color: "#6b7280" }}>
            {loading ? "Loading NBA visit documentation…" : "NBA visit data is currently unavailable. Please check back later."}
          </p>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="nvb-page">
      <Header parentMenu="outreach" topbarEnable="enable" />

      <PageBanner
        title={hero.title}
        eyebrow={hero.eyebrow}
        subtitle={hero.subtitle}
        image={hero.bannerImage ? mediaUrl(hero.bannerImage) : undefined}
      />

      {/* Accreditation snapshot — floating stat card (matches the Alumni page) */}
      <div className="nvb-stats">
        <div className="container">
          <div className="nvb-stats__inner">
            <div className="nvb-stats__item">
              <span className="nvb-stats__num">
                <CountUp end={categories.length} duration={2} enableScrollSpy scrollSpyOnce />
              </span>
              <span className="nvb-stats__label">Categories</span>
            </div>
            <div className="nvb-stats__item">
              <span className="nvb-stats__num">
                <CountUp end={totalDocs} duration={2.2} enableScrollSpy scrollSpyOnce />
              </span>
              <span className="nvb-stats__label">Documents</span>
            </div>
            <div className="nvb-stats__item">
              <span className="nvb-stats__num">2021–24</span>
              <span className="nvb-stats__label">Records</span>
            </div>
          </div>
          <div className="nvb-stats__cta-row">
            <a href="#nvb-hub" className="nvb-hero__cta">Browse Documentation</a>
          </div>
        </div>
      </div>

      <section className="nvb-hub" id="nvb-hub">
        <div className="container">
          <div className="nvb-hub__intro">
            <span className="nvb-hub__eyebrow">{documents.eyebrow}</span>
            <h2>{documents.heading}</h2>
            <p>{documents.intro}</p>
          </div>

          <div className="nvb-hub__search">
            <span className="nvb-hub__search-icon"><IconSearch /></span>
            <input
              type="search"
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              placeholder="Search documents in the selected category..."
              aria-label="Search NBA visit documents"
            />
            {searchQuery && (
              <button type="button" className="nvb-hub__search-clear" onClick={() => setSearchQuery("")}>
                Clear
              </button>
            )}
          </div>

          <div className="nvb-hub__layout">
            <aside className="nvb-sidebar" aria-label="Document categories">
              {categories.map((category) => {
                const meta = CATEGORY_META[category.id] || CATEGORY_META["quick-links"];
                const isActive = category.id === activeCategory?.id;

                return (
                  <button
                    key={category.id}
                    type="button"
                    className={`nvb-sidebar__item ${isActive ? "is-active" : ""}`}
                    onClick={() => {
                      setActiveId(category.id);
                      setSearchQuery("");
                    }}
                    style={{ "--nvb-accent": meta.accent }}
                  >
                    <span className="nvb-sidebar__icon"><CategoryIcon type={meta.icon} /></span>
                    <span className="nvb-sidebar__copy">
                      <strong>{category.title}</strong>
                      <small>{category.fileCount} resources</small>
                    </span>
                    <span className="nvb-sidebar__count">{category.fileCount}</span>
                  </button>
                );
              })}
            </aside>

            <div className="nvb-panel" style={{ "--nvb-accent": activeMeta.accent }}>
              <header className="nvb-panel__head">
                <div className="nvb-panel__title-wrap">
                  <span className="nvb-panel__icon"><CategoryIcon type={activeMeta.icon} /></span>
                  <div>
                    <h3>{activeCategory?.title}</h3>
                    <p>
                      {visibleFileCount} {visibleFileCount === 1 ? "document" : "documents"}
                      {normalizedQuery ? ` matching "${searchQuery.trim()}"` : " available for download"}
                    </p>
                  </div>
                </div>
                <span className="nvb-panel__badge">PDF</span>
              </header>

              <AnimatePresence mode="wait">
                <motion.div
                  key={`${activeCategory?.id}-${normalizedQuery}`}
                  className="nvb-panel__body"
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.28, ease: EASE }}
                >
                  {visibleSections.length === 0 ? (
                    <div className="nvb-empty">
                      <p>No documents match your search in this category.</p>
                      <button type="button" onClick={() => setSearchQuery("")}>Reset search</button>
                    </div>
                  ) : (
                    visibleSections.map((section, sectionIndex) => (
                      <div key={`${section.title || "main"}-${sectionIndex}`} className="nvb-section-block">
                        {section.title && (
                          <div className="nvb-section-block__head">
                            <h4>{section.title}</h4>
                            <span>{section.files.length} items</span>
                          </div>
                        )}

                        <div className="nvb-doc-list">
                          {section.files.map((file, fileIndex) => (
                            <a
                              key={`${file.url}-${fileIndex}`}
                              className="nvb-doc"
                              href={mediaUrl(file.url)}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <span className="nvb-doc__icon"><IconPdf /></span>
                              <span className="nvb-doc__copy">
                                <strong>{file.title}</strong>
                                <small>PDF Document</small>
                              </span>
                              <span className="nvb-doc__action">
                                <IconDownload />
                                <span>Download</span>
                              </span>
                            </a>
                          ))}
                        </div>
                      </div>
                    ))
                  )}
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </div>
      </section>

      <section className="nvb-cta">
        <div className="container">
          <div className="nvb-cta__card">
            <div>
              <span className="nvb-cta__eyebrow">National Board of Accreditation</span>
              <h3>Institutional records, organised for the visit.</h3>
              <p>All documents are official PDFs prepared for NBA evaluation — faculty, placement, student and support staff data in one place.</p>
            </div>
            <a href="#nvb-hub" className="nvb-cta__btn">Open Document Hub</a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default NBAVisitData;
